import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { evaluacionService } from '../services/api';
import { toast } from 'react-toastify';

const EvaluacionForm = () => {
  const { empleadoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [plantilla, setPlantilla] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [comentarios, setComentarios] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const empleado = location.state?.empleado;
  const lider = location.state?.lider;

  useEffect(() => {
    const loadPlantilla = async () => {
      try {
        const data = await evaluacionService.getPlantilla();
        setPlantilla(data);
        
        // Inicializar respuestas vacías
        const initialRespuestas = {};
        data.preguntas.forEach(pregunta => {
          if (pregunta.tipo === 'escala') {
            initialRespuestas[`pregunta_${pregunta.id}`] = '';
          }
        });
        setRespuestas(initialRespuestas);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlantilla();
  }, []);

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [`pregunta_${preguntaId}`]: parseInt(valor)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que todas las preguntas estén respondidas
    const preguntasEscala = plantilla.preguntas.filter(p => p.tipo === 'escala');
    const respuestasVacias = preguntasEscala.filter(p => !respuestas[`pregunta_${p.id}`]);
    
    if (respuestasVacias.length > 0) {
      toast.error('Por favor responde todas las preguntas antes de enviar');
      return;
    }

    setIsSubmitting(true);

    try {
      const evaluacionData = {
        empleado_id: empleadoId,
        lider_id: lider.id,
        ...respuestas,
        comentarios: comentarios.trim() || null
      };

      await evaluacionService.submitEvaluacion(evaluacionData);
      toast.success('Evaluación enviada exitosamente');
      navigate('/dashboard-lider');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard-lider');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!empleado || !lider || !plantilla) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {plantilla.titulo}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Evaluando a: <span className="font-medium">{empleado.nombre_completo}</span>
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out"
            >
              Cancelar
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Información del Empleado
              </h3>
              <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Nombre:</span>
                  <p className="text-sm text-gray-900">{empleado.nombre_completo}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Cargo:</span>
                  <p className="text-sm text-gray-900">{empleado.cargo}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Documento:</span>
                  <p className="text-sm text-gray-900">{empleado.documento_identidad}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Preguntas de Evaluación
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    {plantilla.descripcion}
                  </p>

                  <div className="space-y-6">
                    {plantilla.preguntas.filter(p => p.tipo === 'escala').map((pregunta) => (
                      <div key={pregunta.id} className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          {pregunta.texto}
                        </label>
                        <div className="grid grid-cols-5 gap-3">
                          {pregunta.opciones.map((opcion, index) => {
                            const valor = index + 1;
                            return (
                              <label
                                key={valor}
                                className="relative flex items-center justify-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                              >
                                <input
                                  type="radio"
                                  name={`pregunta_${pregunta.id}`}
                                  value={valor}
                                  checked={respuestas[`pregunta_${pregunta.id}`] === valor}
                                  onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                                  className="sr-only"
                                  required
                                />
                                <div className="text-center">
                                  <div className={`w-6 h-6 rounded-full border-2 mx-auto mb-1 ${
                                    respuestas[`pregunta_${pregunta.id}`] === valor
                                      ? 'bg-primary-600 border-primary-600'
                                      : 'border-gray-300'
                                  }`}>
                                    {respuestas[`pregunta_${pregunta.id}`] === valor && (
                                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-1"></div>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-600">{valor}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                          <span>Deficiente</span>
                          <span>Excelente</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Comentarios */}
                <div>
                  <label htmlFor="comentarios" className="block text-sm font-medium text-gray-700 mb-2">
                    Comentarios adicionales (opcional)
                  </label>
                  <textarea
                    id="comentarios"
                    rows={4}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Agrega comentarios sobre el desempeño del empleado..."
                    value={comentarios}
                    onChange={(e) => setComentarios(e.target.value)}
                  />
                </div>

                {/* Botones */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-white border border-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md hover:bg-gray-50 transition duration-150 ease-in-out"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Evaluación'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EvaluacionForm;